import { create } from 'zustand';
import { Node, Edge, OnNodesChange, OnEdgesChange, applyNodeChanges, applyEdgeChanges } from 'reactflow';
import { AutomationFlow } from '../types/nodes';
import dbService from '../services/dbService';

interface FlowStore {
  nodes: Node[];
  edges: Edge[];
  flows: AutomationFlow[];
  currentFlowId: string | null;
  testMode: boolean;
  
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  addNode: (node: Node) => void;
  updateNode: (nodeId: string, data: any) => void;
  deleteNode: (nodeId: string) => void;
  
  saveFlow: (name: string, description?: string) => Promise<void>;
  loadFlow: (flowId: string) => void;
  loadFlowAsync: (flowId: string) => Promise<void>;
  refreshFlows: () => Promise<void>;
  deleteFlow: (flowId: string) => Promise<void>;
  updateFlow: (flowId: string, updates: Partial<AutomationFlow>) => void;
  
  setTestMode: (enabled: boolean) => void;
  testFlow: () => void;
}

const useFlowStore = create<FlowStore>((set, get) => ({
  nodes: [],
  edges: [],
  flows: [],
  currentFlowId: null,
  testMode: false,
  
  onNodesChange: (changes) => {
    set({
      nodes: applyNodeChanges(changes, get().nodes),
    });
  },
  
  onEdgesChange: (changes) => {
    set({
      edges: applyEdgeChanges(changes, get().edges),
    });
  },
  
  setNodes: (nodes) => set({ nodes }),
  setEdges: (edges) => set({ edges }),
  
  addNode: (node) => {
    set({ nodes: [...get().nodes, node] });
  },
  
  updateNode: (nodeId, data) => {
    set({
      nodes: get().nodes.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      ),
    });
  },
  
  deleteNode: (nodeId) => {
    set({
      nodes: get().nodes.filter((node) => node.id !== nodeId),
      edges: get().edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
    });
  },
  
  saveFlow: async (name, description) => {
    const { nodes, edges, currentFlowId } = get();
    
    if (currentFlowId) {
      // Aggiorna il flow esistente
      try {
        const response = await fetch(`/api/flows/${currentFlowId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            description,
            nodes,
            edges,
          }),
        });
        const data = await response.json();
        if (data.success) {
          // Aggiorna lo store locale
          await get().refreshFlows();
        }
      } catch (error) {
        console.error('Error updating flow:', error);
      }
    } else {
      // Crea un nuovo flow
      try {
        const response = await fetch('/api/flows', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            description,
            nodes,
            edges,
            isActive: false,
          }),
        });
        const data = await response.json();
        if (data.success && data.flow) {
          set({ currentFlowId: data.flow.id });
          await get().refreshFlows();
        }
      } catch (error) {
        console.error('Error creating flow:', error);
      }
    }
  },
  
  loadFlow: (flowId) => {
    const flow = dbService.getFlow(flowId);
    if (flow) {
      set({
        nodes: flow.nodes,
        edges: flow.edges,
        currentFlowId: flowId,
      });
    } else {
      // Flow not in cache, try to load from backend
      get().loadFlowAsync(flowId);
    }
  },
  
  loadFlowAsync: async (flowId) => {
    try {
      const response = await fetch(`/api/flows/${flowId}`);
      const data = await response.json();
      if (data.success && data.flow) {
        set({
          nodes: data.flow.nodes,
          edges: data.flow.edges,
          currentFlowId: flowId,
        });
      }
    } catch (error) {
      console.error('Error loading flow:', error);
    }
  },
  
  refreshFlows: async () => {
    try {
      const flows = await dbService.getAllFlowsAsync();
      set({ flows });
    } catch (error) {
      console.error('Error refreshing flows:', error);
    }
  },
  
  deleteFlow: async (flowId) => {
    try {
      const response = await fetch(`/api/flows/${flowId}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        // Refresh flows from backend
        await get().refreshFlows();
      }
    } catch (error) {
      console.error('Error deleting flow:', error);
    }
  },
  
  updateFlow: (flowId, updates) => {
    const currentFlow = get().currentFlowId === flowId;
    
    // Se stiamo aggiornando il flow corrente, salva i nodi e edges attuali
    if (currentFlow) {
      updates.nodes = get().nodes;
      updates.edges = get().edges;
    }
    
    dbService.updateFlow(flowId, updates);
    const updatedFlows = dbService.getAllFlows();
    set({ flows: updatedFlows });
  },
  
  setTestMode: (enabled) => set({ testMode: enabled }),
  
  testFlow: () => {
    const { nodes, edges } = get();
    console.log('Testing flow with nodes:', nodes);
    console.log('Testing flow with edges:', edges);
    // Qui implementeremo la logica di test
  },
}));

// Carica flows dal database all'avvio
const initialFlows = dbService.getAllFlows();
useFlowStore.setState({ flows: initialFlows });

export default useFlowStore;
